(function () {
  const routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  const joinRoute = (path) => `${routesRoot.replace(/\/?$/, "/")}${String(path).replace(/^\//, "")}`;

  const sanitizeFont = (font) => String(font || "Basteleur Moonlight").replace(/[;"{}]/g, "");

  const fontStack = (font) => {
    const safe = sanitizeFont(font);
    if (safe === "Courier Prime") return '"Courier Prime", "Courier New", Courier, monospace';
    return `"${safe}", Georgia, serif`;
  };

  const engravingTemplate = Object.freeze({
    centerX: 0.5,
    centerY: 0.505,
    stoneRadiusX: 0.205,
    stoneRadiusY: 0.265,
    safeInset: 0.18
  });

  const engravingEllipse = (width, height) => ({
    cx: width * engravingTemplate.centerX,
    cy: height * engravingTemplate.centerY,
    rx: width * engravingTemplate.stoneRadiusX * (1 - engravingTemplate.safeInset),
    ry: height * engravingTemplate.stoneRadiusY * (1 - engravingTemplate.safeInset)
  });

  const rotatedRectFitsEllipse = (rect, ellipse) => {
    const cos = Math.cos(rect.rotation);
    const sin = Math.sin(rect.rotation);
    const corners = [
      [-rect.halfWidth, -rect.halfHeight],
      [rect.halfWidth, -rect.halfHeight],
      [rect.halfWidth, rect.halfHeight],
      [-rect.halfWidth, rect.halfHeight]
    ];

    return corners.every(([localX, localY]) => {
      const x = rect.x + localX * cos - localY * sin;
      const y = rect.y + localX * sin + localY * cos;
      const normalizedX = (x - ellipse.cx) / ellipse.rx;
      const normalizedY = (y - ellipse.cy) / ellipse.ry;
      return normalizedX * normalizedX + normalizedY * normalizedY <= 1;
    });
  };

  const measureTextBlock = (ctx, lines, fontSize, stack) => {
    const lineHeight = fontSize * 1.14;
    ctx.font = `400 ${fontSize}px ${stack}`;
    const widestLine = Math.max(...lines.map((line) => ctx.measureText(line).width));
    const strokeAllowance = Math.max(3, fontSize * 0.08) * 1.5;
    return {
      fontSize,
      lineHeight,
      halfWidth: widestLine / 2 + strokeAllowance,
      halfHeight: (lineHeight * lines.length) / 2 + strokeAllowance
    };
  };

  const wrapTextToEllipse = (ctx, value, fontSize, stack, maxWidth, maxLines = 4) => {
    const text = String(value || "").trim();
    if (!text) return { lines: [], overflow: false };

    ctx.font = `400 ${fontSize}px ${stack}`;
    const lines = [];
    let overflow = false;

    const pushLine = (line) => {
      if (lines.length >= maxLines) {
        overflow = true;
        return false;
      }
      lines.push(line);
      return true;
    };

    const pushLongToken = (token) => {
      let segment = "";
      for (const character of token) {
        const candidate = segment + character;
        if (segment && ctx.measureText(candidate).width > maxWidth) {
          if (!pushLine(segment)) return "";
          segment = character;
        } else {
          segment = candidate;
        }
      }
      return segment;
    };

    for (const paragraph of text.split(/\r?\n/)) {
      const words = paragraph.trim().split(/\s+/).filter(Boolean);
      if (!words.length) {
        if (!pushLine("")) break;
        continue;
      }

      let current = "";
      for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;
        if (ctx.measureText(candidate).width <= maxWidth) {
          current = candidate;
          continue;
        }

        if (current && !pushLine(current)) break;
        current = "";
        if (ctx.measureText(word).width <= maxWidth) {
          current = word;
        } else {
          current = pushLongToken(word);
        }
        if (overflow) break;
      }

      if (overflow) break;
      if (current && !pushLine(current)) break;
    }

    return { lines: lines.slice(0, maxLines), overflow };
  };

  const rectFitsAt = (x, y, metrics, ellipse, rotation) => rotatedRectFitsEllipse({
    x,
    y,
    halfWidth: metrics.halfWidth,
    halfHeight: metrics.halfHeight,
    rotation
  }, ellipse);

  const validAxisRange = (axis, fixedCoordinate, metrics, ellipse, rotation) => {
    const center = axis === "x" ? ellipse.cx : ellipse.cy;
    const radius = axis === "x" ? ellipse.rx : ellipse.ry;
    const fits = (coordinate) => axis === "x"
      ? rectFitsAt(coordinate, fixedCoordinate, metrics, ellipse, rotation)
      : rectFitsAt(fixedCoordinate, coordinate, metrics, ellipse, rotation);

    if (!fits(center)) return { min: center, max: center, movable: false };

    const findEdge = (direction) => {
      let low = 0;
      let high = radius;
      for (let index = 0; index < 30; index += 1) {
        const distance = (low + high) / 2;
        if (fits(center + distance * direction)) {
          low = distance;
        } else {
          high = distance;
        }
      }
      return center + low * direction;
    };

    const min = findEdge(-1);
    const max = findEdge(1);
    return { min, max, movable: max - min > 0.5 };
  };

  const mapSliderToValidPosition = (xValue, yValue, metrics, ellipse, rotation) => {
    const normalizedX = Math.max(0, Math.min(1, Number(xValue) / 100));
    const normalizedY = Math.max(0, Math.min(1, Number(yValue) / 100));
    let x = ellipse.cx;
    let y = ellipse.cy;
    let xRange = { min: x, max: x, movable: false };
    let yRange = { min: y, max: y, movable: false };

    for (let index = 0; index < 6; index += 1) {
      yRange = validAxisRange("y", x, metrics, ellipse, rotation);
      y = yRange.min + (yRange.max - yRange.min) * normalizedY;
      xRange = validAxisRange("x", y, metrics, ellipse, rotation);
      x = xRange.min + (xRange.max - xRange.min) * normalizedX;
    }

    return { x, y, xRange, yRange };
  };

  const initStudio = (root) => {
    if (!root || root.dataset.ready === "true") return;
    root.dataset.ready = "true";

    const productScript = root.querySelector("[data-kova-studio-product]");
    if (!productScript) return;

    let product;
    try {
      product = JSON.parse(productScript.textContent);
    } catch (error) {
      console.error("Unable to read Kova studio product data.", error);
      return;
    }

    const canvas = root.querySelector("[data-kova-studio-canvas]");
    const ctx = canvas && canvas.getContext("2d");
    const stoneSelect = root.querySelector("[data-kova-stone-select]");
    const selectedName = root.querySelector("[data-kova-selected-name]");
    const stonePicker = root.querySelector("[data-kova-stone-picker]");
    const stoneTrigger = root.querySelector("[data-kova-stone-trigger]");
    const stoneListbox = root.querySelector("[data-kova-stone-listbox]");
    const stoneOptions = Array.from(root.querySelectorAll("[data-kova-stone-option]"));
    const triggerName = root.querySelector("[data-kova-trigger-name]");
    const triggerEnergy = root.querySelector("[data-kova-trigger-energy]");
    const previewEnergyPills = Array.from(root.querySelectorAll("[data-kova-preview-energy]"));
    const textInput = root.querySelector("[data-kova-text]");
    const textError = root.querySelector("[data-kova-text-error]");
    const count = root.querySelector("[data-kova-count]");
    const fontSelect = root.querySelector("[data-kova-font]");
    const colorInputs = Array.from(root.querySelectorAll("[data-kova-color]"));
    const sizeInput = root.querySelector("[data-kova-size]");
    const xInput = root.querySelector("[data-kova-x]");
    const yInput = root.querySelector("[data-kova-y]");
    const rotateInput = root.querySelector("[data-kova-rotate]");
    const engravingSection = root.querySelector("[data-kova-engraving-section]");
    const engravingLock = root.querySelector("[data-kova-engraving-lock]");
    const downloadButton = root.querySelector("[data-kova-download]");
    const addButton = root.querySelector("[data-kova-add]");
    const status = root.querySelector("[data-kova-status]");
    const currentPrice = root.querySelector("[data-kova-current-price]");
    const compareAtPrice = root.querySelector("[data-kova-compare-at-price]");
    const newModeHeading = root.querySelector("[data-kova-new-mode-heading]");
    const editModeHeading = root.querySelector("[data-kova-edit-mode-heading]");
    const editModeCopy = root.querySelector("[data-kova-edit-mode-copy]");
    const backToCart = root.querySelector("[data-kova-back-to-cart]");
    const engravingTextRules = globalThis.KovaEngravingText;
    const KovaCartItemEdit = globalThis.KovaCartItemEdit;
    const KovaCartPreview = globalThis.KovaCartPreview;

    const engravingControls = [
      textInput,
      fontSelect,
      sizeInput,
      xInput,
      yInput,
      rotateInput,
      ...colorInputs
    ].filter(Boolean);

    if (!canvas || !ctx || !textInput || !textError || !fontSelect || !addButton || !engravingTextRules) return;

    let selectedVariant = null;
    let previewImage = new Image();
    let previewImageReady = false;
    let currentImageUrl = product.defaultImage || product.featuredImage || "";
    let engravingFits = true;
    let lastEngravingPosition = { xPercent: 50, yPercent: 52 };
    let editContext = null;
    let lockedState = null;
    let updateInFlight = false;
    let previewLoading = false;
    let previewImageRequest = null;
    const cartItemModeRequested = new URLSearchParams(window.location.search).get("mode") === "cart-item";
    const previewLoadGate = KovaCartItemEdit && typeof KovaCartItemEdit.createPreviewLoadGate === "function"
      ? KovaCartItemEdit.createPreviewLoadGate()
      : null;

    const findVariant = (variantId) => product.variants.find((variant) => String(variant.id) === String(variantId));

    const currentColor = () => colorInputs.find((input) => input.checked) || colorInputs[0] || {
      value: "#F7F6F2",
      dataset: { colorName: "White" }
    };

    const setStatus = (message, tone = "") => {
      if (!status) return;
      status.textContent = message || "";
      if (tone) {
        status.dataset.tone = tone;
      } else {
        delete status.dataset.tone;
      }
    };

    const setPickerOpen = (open, focusOption = false) => {
      if (!stoneTrigger || !stoneListbox) return;
      stoneTrigger.setAttribute("aria-expanded", open ? "true" : "false");
      stoneListbox.hidden = !open;
      if (open && focusOption) {
        const selectedOptionButton = stoneOptions.find((option) => option.getAttribute("aria-selected") === "true");
        (selectedOptionButton || stoneOptions[0])?.focus();
      }
    };

    const setEngravingEnabled = (enabled) => {
      engravingControls.forEach((control) => {
        control.disabled = editContext ? true : !enabled;
      });
      if (engravingSection) {
        engravingSection.classList.toggle("is-disabled", !enabled && !editContext);
        engravingSection.classList.toggle("is-cart-item-edit", Boolean(editContext));
        if (editContext) engravingSection.removeAttribute("aria-disabled");
        else engravingSection.setAttribute("aria-disabled", enabled ? "false" : "true");
      }
      if (engravingLock) engravingLock.hidden = enabled || Boolean(editContext);
    };

    const showEditMode = () => {
      root.classList.add("is-cart-item-edit");
      if (newModeHeading) newModeHeading.hidden = true;
      if (editModeHeading) editModeHeading.hidden = false;
      if (editModeCopy) editModeCopy.hidden = false;
      if (backToCart) backToCart.hidden = false;
      addButton.textContent = "Update cart item";
    };

    const setEditStonePickerEnabled = (enabled) => {
      if (stoneTrigger) stoneTrigger.disabled = !enabled;
      if (stoneSelect) stoneSelect.disabled = !enabled;
      stoneOptions.forEach((option) => {
        option.disabled = !enabled || option.dataset.available !== "true";
      });
      if (!enabled) setPickerOpen(false);
    };

    const showEditError = (message) => {
      showEditMode();
      engravingControls.forEach((control) => {
        control.disabled = true;
      });
      setEditStonePickerEnabled(false);
      if (engravingLock) engravingLock.hidden = true;
      addButton.disabled = true;
      setStatus(message, "error");
      if (status) {
        status.tabIndex = -1;
        status.focus({preventScroll: true});
      }
    };

    const updateCount = () => {
      if (!count) return;
      const characterCount = Array.from(engravingTextRules.normalizeEngravingText(textInput.value)).length;
      count.textContent = `${characterCount}/${textInput.maxLength}`;
    };

    const updateTextValidity = () => {
      const unsupportedCharacters = engravingTextRules.formatUnsupportedCharacters(textInput.value);
      const valid = !unsupportedCharacters;
      if (valid) {
        textInput.removeAttribute("aria-invalid");
      } else {
        textInput.setAttribute("aria-invalid", "true");
      }
      textInput.classList.toggle("has-error", !valid);
      textError.textContent = valid
        ? ""
        : `Unsupported characters: ${unsupportedCharacters}\nPlease use English letters, numbers, spaces, and supported punctuation only.`;
      textError.hidden = valid;
      return valid;
    };

    const updateSelectedMeta = () => {
      if (selectedName) selectedName.textContent = selectedVariant ? selectedVariant.title : "Choose a stone";
      if (triggerName) triggerName.textContent = selectedVariant ? selectedVariant.title : "Choose a stone";
      if (triggerEnergy) {
        triggerEnergy.textContent = selectedVariant && selectedVariant.energy ? selectedVariant.energy : "";
        triggerEnergy.hidden = !selectedVariant || !selectedVariant.energy;
      }

      const energies = selectedVariant && selectedVariant.energy
        ? selectedVariant.energy.split("·").map((term) => term.trim()).filter(Boolean).slice(0, 2)
        : [];
      previewEnergyPills.forEach((pill, index) => {
        pill.textContent = energies[index] || "";
        pill.hidden = !energies[index];
      });

      stoneOptions.forEach((option) => {
        option.setAttribute("aria-selected", selectedVariant && String(selectedVariant.id) === option.dataset.variantId ? "true" : "false");
      });

      if (selectedVariant && currentPrice) currentPrice.textContent = selectedVariant.price;
      if (compareAtPrice && selectedVariant) {
        compareAtPrice.textContent = selectedVariant.compareAtPrice || "";
        compareAtPrice.hidden = !selectedVariant.compareAtPrice;
      }
    };

    const updateAddState = () => {
      if (cartItemModeRequested) {
        const changedVariant = Boolean(
          editContext &&
          selectedVariant &&
          String(selectedVariant.id) !== String(editContext.originalVariantId)
        );
        const previewReadyForVariant = Boolean(
          editContext &&
          selectedVariant &&
          previewImageReady &&
          previewLoadGate &&
          previewLoadGate.isReadyFor(selectedVariant.id)
        );
        addButton.disabled = updateInFlight || !changedVariant || !selectedVariant?.available || !previewReadyForVariant;
        if (!editContext) return;
        if (!selectedVariant?.available) {
          setStatus("This stone is sold out. Choose another available stone.", "error");
        } else if (!changedVariant) {
          setStatus("Choose a different stone to update this cart item.");
        } else if (previewLoading || !previewReadyForVariant) {
          setStatus("Preparing the selected stone preview...");
        } else if (!updateInFlight) {
          setStatus("");
        }
        return;
      }
      const engravingText = textInput.value.trim();
      const supportedText = updateTextValidity();
      const previewReadyForVariant = Boolean(
        selectedVariant &&
        previewImageReady &&
        previewLoadGate &&
        previewLoadGate.isReadyFor(selectedVariant.id)
      );
      const canAdd = Boolean(
        selectedVariant &&
        selectedVariant.available &&
        engravingText &&
        supportedText &&
        engravingFits &&
        previewReadyForVariant
      );
      addButton.disabled = !canAdd;

      if (!selectedVariant) {
        setStatus("Choose a stone to continue.", "error");
      } else if (!selectedVariant.available) {
        setStatus("This stone is sold out.", "error");
      } else if (!engravingText) {
        setStatus("Enter engraving text before adding to cart.");
      } else if (!supportedText) {
        setStatus("");
      } else if (!engravingFits) {
        setStatus("Text doesn’t fit. Reduce the size or shorten the text.", "error");
      } else if (previewLoading || !previewReadyForVariant) {
        setStatus("Preparing the selected stone preview...");
      } else {
        setStatus("");
      }
    };

    const drawText = () => {
      engravingFits = true;
      if (!selectedVariant) return;

      const color = currentColor();
      const width = canvas.width;
      const height = canvas.height;
      const fontSize = Math.max(20, Math.round(width * (Number(sizeInput.value) / 100)));
      const rotation = (Number(rotateInput.value) * Math.PI) / 180;
      const ellipse = engravingEllipse(width, height);
      const stack = fontStack(fontSelect.value);
      const wrapped = wrapTextToEllipse(ctx, textInput.value, fontSize, stack, ellipse.rx * 1.72, 4);
      const lines = wrapped.lines;
      if (!lines.length) {
        xInput.disabled = Boolean(editContext);
        yInput.disabled = Boolean(editContext);
        return;
      }

      const metrics = measureTextBlock(ctx, lines, fontSize, stack);
      const fitsAtCenter = rectFitsAt(ellipse.cx, ellipse.cy, metrics, ellipse, rotation);
      engravingFits = !wrapped.overflow && fitsAtCenter;
      const position = engravingFits
        ? mapSliderToValidPosition(xInput.value, yInput.value, metrics, ellipse, rotation)
        : { x: ellipse.cx, y: ellipse.cy, xRange: { movable: false }, yRange: { movable: false } };

      xInput.disabled = Boolean(editContext) || !engravingFits || !position.xRange.movable;
      yInput.disabled = Boolean(editContext) || !engravingFits || !position.yRange.movable;
      if (engravingFits && !position.xRange.movable) xInput.value = "50";
      if (engravingFits && !position.yRange.movable) yInput.value = "50";
      lastEngravingPosition = {
        xPercent: (position.x / width) * 100,
        yPercent: (position.y / height) * 100
      };

      ctx.save();
      ctx.beginPath();
      ctx.ellipse(ellipse.cx, ellipse.cy, ellipse.rx, ellipse.ry, 0, 0, Math.PI * 2);
      ctx.clip();
      ctx.translate(position.x, position.y);
      ctx.rotate(rotation);
      ctx.font = `400 ${fontSize}px ${stack}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.shadowColor = "rgba(0, 0, 0, 0.24)";
      ctx.shadowBlur = 7;
      ctx.shadowOffsetY = 2;

      lines.forEach((line, index) => {
        const offset = (index - (lines.length - 1) / 2) * metrics.lineHeight;
        ctx.strokeStyle = "rgba(20, 24, 20, 0.2)";
        ctx.lineWidth = Math.max(3, fontSize * 0.08);
        ctx.strokeText(line, 0, offset);
        ctx.fillStyle = color.value;
        ctx.fillText(line, 0, offset);
      });

      ctx.restore();
    };

    const drawPreview = () => {
      if (
        editContext &&
        previewImageReady &&
        previewImage.naturalWidth &&
        previewImage.naturalHeight &&
        KovaCartPreview &&
        typeof KovaCartPreview.drawNormalizedPreview === "function"
      ) {
        try {
          KovaCartPreview.drawNormalizedPreview(
            canvas,
            {image: currentImageUrl, designId: editContext.designId},
            previewImage,
            editContext.previewDesign
          );
          updateCount();
          updateAddState();
          return;
        } catch (error) {
          showEditError("We couldn't render this cart item's saved engraving. Return to your cart and try again.");
          return;
        }
      }
      const width = canvas.width;
      const height = canvas.height;
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = "#f4f1ea";
      ctx.fillRect(0, 0, width, height);

      if (previewImageReady && previewImage.naturalWidth && previewImage.naturalHeight) {
        const ratio = Math.max(
          width / previewImage.naturalWidth,
          height / previewImage.naturalHeight
        );
        const imageWidth = previewImage.naturalWidth * ratio;
        const imageHeight = previewImage.naturalHeight * ratio;
        ctx.save();
        ctx.shadowColor = "rgba(36, 48, 38, 0.1)";
        ctx.shadowBlur = 22;
        ctx.shadowOffsetY = 10;
        ctx.drawImage(previewImage, (width - imageWidth) / 2, (height - imageHeight) / 2, imageWidth, imageHeight);
        ctx.restore();
      } else {
        ctx.save();
        ctx.fillStyle = "#d9d4c8";
        ctx.beginPath();
        ctx.ellipse(width / 2, height / 2, width * 0.33, height * 0.24, -0.08, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      drawText();
      updateCount();
      updateAddState();
    };

    const drawPreviewAfterFontLoad = async () => {
      const fontSize = Math.max(20, Math.round(canvas.width * (Number(sizeInput.value) / 100)));

      if (document.fonts && document.fonts.load) {
        try {
          await document.fonts.load(`400 ${fontSize}px ${fontStack(fontSelect.value)}`);
        } catch (error) {
          // Fall back to the browser's available font while keeping the preview responsive.
        }
      }

      drawPreview();
    };

    const loadLockedPreviewFont = async () => {
      if (!lockedState || !document.fonts || typeof document.fonts.load !== "function") return;
      const versionedFamilies = {
        "Basteleur Moonlight": "Kova Basteleur Moonlight v1",
        "Courier Prime": "Kova Courier Prime Regular v1",
        "Velvelyne": "Kova Velvelyne Regular v1",
        "La Belle Aurore": "Kova La Belle Aurore Regular v1",
        "Telma": "Kova Telma Regular v1"
      };
      const versionedFamily = versionedFamilies[lockedState.fontFamily];
      if (!versionedFamily) throw new Error("The saved engraving font is unavailable.");
      const loadedFaces = await document.fonts.load(`400 24px "${versionedFamily}"`);
      if (!loadedFaces || loadedFaces.length < 1) throw new Error("The saved engraving font could not be loaded.");
    };

    const loadPreviewImage = (imageUrl) => {
      const requestedImageUrl = imageUrl || product.defaultImage || product.featuredImage || "";
      const requestedVariantId = selectedVariant?.id || "__default__";
      previewImageRequest = previewLoadGate && previewLoadGate.begin(requestedVariantId, requestedImageUrl);
      const imageRequest = previewImageRequest;
      previewLoading = true;
      previewImageReady = false;
      if (cartItemModeRequested) updateAddState();
      if (!requestedImageUrl) {
        previewLoading = false;
        if (editContext) {
          showEditError("We couldn't load the selected stone preview. Your cart item is unchanged.");
        } else {
          drawPreview();
        }
        return;
      }

      const requestedImage = new Image();
      requestedImage.crossOrigin = "anonymous";
      requestedImage.onload = async () => {
        try {
          if (editContext) await loadLockedPreviewFont();
          if (!previewLoadGate || !previewLoadGate.accept(imageRequest)) return;
          previewImage = requestedImage;
          currentImageUrl = requestedImageUrl;
          previewLoading = false;
          previewImageReady = true;
          drawPreview();
        } catch (error) {
          if (previewLoadGate && !previewLoadGate.isCurrent(imageRequest)) return;
          previewLoading = false;
          previewImageReady = false;
          showEditError("We couldn't render this cart item's saved engraving. Return to your cart and try again.");
        }
      };
      requestedImage.onerror = () => {
        if (previewLoadGate && !previewLoadGate.isCurrent(imageRequest)) return;
        previewLoading = false;
        previewImageReady = false;
        if (editContext) {
          showEditError("We couldn't load the selected stone preview. Your cart item is unchanged.");
        } else {
          drawPreview();
        }
      };
      requestedImage.src = requestedImageUrl;
    };

    const selectVariant = (variantId) => {
      selectedVariant = findVariant(variantId) || null;
      setEngravingEnabled(Boolean(selectedVariant) && !editContext);
      if (selectedVariant && selectedVariant.id && !selectedVariant.sku) {
        console.warn("Kova studio variant has no SKU:", selectedVariant.title);
      }

      updateSelectedMeta();
      loadPreviewImage(selectedVariant && selectedVariant.image ? selectedVariant.image : product.defaultImage);
    };

    const cartRequest = async (path, body) => {
      const response = await fetch(joinRoute(path), {
        method: "POST",
        headers: {Accept: "application/json", "Content-Type": "application/json"},
        body: JSON.stringify(body)
      });
      const responseBody = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(responseBody.description || responseBody.message || "The cart could not be updated.");
      return responseBody;
    };

    const preloadCheckoutPreview = (designId) => new Promise((resolve) => {
      const image = new Image();
      const finish = () => resolve();
      image.addEventListener("load", finish, {once: true});
      image.addEventListener("error", finish, {once: true});
      image.src = `/apps/kova-designs/designs/${encodeURIComponent(designId)}/checkout-preview`;
    });

    const updateCartItem = async () => {
      if (updateInFlight || !editContext || !lockedState) return;
      if (
        !selectedVariant?.available ||
        String(selectedVariant.id) === String(editContext.originalVariantId) ||
        !previewImageReady ||
        !previewLoadGate?.isReadyFor(selectedVariant.id)
      ) {
        updateAddState();
        return;
      }

      updateInFlight = true;
      addButton.disabled = true;
      setStatus("Saving the replacement design...");

      try {
        let draftInput;
        try {
          const checkoutPreviewPng = KovaDesignContract.captureCheckoutPreviewPng(canvas);
          draftInput = KovaDesignContract.buildInput({
            variantId: selectedVariant.id,
            text: lockedState.text,
            fontFamily: lockedState.fontFamily,
            colorHex: lockedState.colorHex,
            colorName: lockedState.colorName,
            sizePercent: lockedState.sizePercent,
            xPercent: lockedState.xPercent,
            yPercent: lockedState.yPercent,
            rotationDeg: lockedState.rotationDeg,
            checkoutPreviewPng
          });
        } catch (error) {
          const preparationError = new Error("We couldn't prepare your replacement design. Your original cart item is unchanged. Please try again.", {cause: error});
          preparationError.name = "DesignPreparationError";
          throw preparationError;
        }

        await KovaCartItemEdit.replaceCartItem({
          lineKey: editContext.lineKey,
          expectedDesignId: editContext.designId,
          draftInput,
          replacementLine: {
            id: Number(selectedVariant.id),
            properties: editContext.readableProperties
          }
        }, {
          saveDraft: async (input) => {
            try {
              const idempotencyKey = `idem_${crypto.randomUUID().replaceAll("-", "")}`;
              const saved = await KovaDesignContract.saveDraft(input, idempotencyKey);
              await preloadCheckoutPreview(saved.designId);
              return saved;
            } catch (error) {
              const boundaryError = new Error("We couldn't save the replacement design. Your original cart item is unchanged. Please try again.", {cause: error});
              boundaryError.name = "DesignSaveError";
              throw boundaryError;
            }
          },
          addLine: async (line) => {
            setStatus("Adding the replacement stone...");
            return cartRequest("cart/add.js", {items: [line]});
          },
          readCart: async () => {
            const response = await fetch(joinRoute("cart.js"), {
              cache: "no-store",
              headers: {Accept: "application/json"}
            });
            const responseBody = await response.json().catch(() => null);
            if (!response.ok || !responseBody) throw new Error("The cart could not be read.");
            return responseBody;
          },
          changeLine: async (lineKey, quantity) => cartRequest("cart/change.js", {id: lineKey, quantity})
        });
        window.location.href = joinRoute("cart");
      } catch (error) {
        let message;
        const classifiedCartErrors = new Set([
          "CartAdditionError",
          "CartConcurrentChangeError",
          "CartManualReviewError",
          "CartReadError",
          "CartReplacementError"
        ]);
        message = error.message || "We couldn't confirm the cart state. Please review your cart before retrying.";
        if (!classifiedCartErrors.has(error.name) && !error.message) {
          message = "We couldn't confirm the cart state. Please review your cart before retrying.";
        }
        setStatus(message, "error");
        if (status) {
          status.tabIndex = -1;
          status.focus({preventScroll: true});
        }
      } finally {
        updateInFlight = false;
        addButton.disabled = !selectedVariant?.available ||
          String(selectedVariant?.id) === String(editContext.originalVariantId) ||
          !previewImageReady ||
          !previewLoadGate?.isReadyFor(selectedVariant?.id);
      }
    };

    const initializeCartItemEdit = async () => {
      showEditMode();
      engravingControls.forEach((control) => {
        control.disabled = true;
      });
      setEditStonePickerEnabled(false);
      if (engravingLock) engravingLock.hidden = true;
      addButton.disabled = true;
      setStatus("Loading your saved design...");
      const request = KovaCartItemEdit && KovaCartItemEdit.parseCartItemEdit(window.location.search);
      if (!request || !KovaCartPreview || !KovaCartItemEdit) {
        showEditError("We couldn't load this cart item's saved design. Return to your cart and try again.");
        return;
      }

      try {
        editContext = await KovaCartItemEdit.loadCartItemEdit(request);
        lockedState = KovaCartItemEdit.lockedStateFrom(editContext.previewDesign);
        textInput.value = lockedState.text;
        const matchingFont = Array.from(fontSelect.options).find((option) => option.value === lockedState.fontFamily);
        if (matchingFont) fontSelect.value = matchingFont.value;
        colorInputs.forEach((input) => {
          input.checked = input.value.toUpperCase() === lockedState.colorHex.toUpperCase();
        });
        sizeInput.value = String(lockedState.sizePercent);
        xInput.value = String(lockedState.xPercent);
        yInput.value = String(lockedState.yPercent);
        rotateInput.value = String(lockedState.rotationDeg);
        lastEngravingPosition = {xPercent: lockedState.xPercent, yPercent: lockedState.yPercent};
        engravingControls.forEach((control) => {
          control.disabled = true;
        });
        setEngravingEnabled(false);
        setEditStonePickerEnabled(true);
        if (stoneSelect) stoneSelect.value = String(editContext.originalVariantId);
        if (!findVariant(editContext.originalVariantId)) {
          throw new Error("The original stone is no longer part of this product.");
        }
        selectVariant(editContext.originalVariantId);
      } catch (error) {
        editContext = null;
        lockedState = null;
        showEditError("We couldn't load this cart item's saved design. It may no longer be in your cart. Return to your cart and try again.");
      }
    };

    const addToCart = async () => {
      const engravingText = engravingTextRules.normalizeEngravingText(textInput.value).trim();
      const color = currentColor();

      if (!selectedVariant || !selectedVariant.id) {
        setStatus("Choose a stone before adding to cart.", "error");
        return;
      }

      if (!selectedVariant.available) {
        setStatus("This stone is sold out.", "error");
        return;
      }

      if (!engravingText) {
        setStatus("Please enter engraving text.", "error");
        textInput.focus();
        return;
      }

      if (!engravingTextRules.isSupportedEngravingText(engravingText)) {
        updateTextValidity();
        setStatus("");
        textInput.focus();
        return;
      }

      if (!engravingFits) {
        setStatus("Text doesn’t fit. Reduce the size or shorten the text.", "error");
        return;
      }

      if (
        !previewImageReady ||
        !previewLoadGate ||
        !previewLoadGate.isReadyFor(selectedVariant.id)
      ) {
        setStatus("Preparing the selected stone preview...");
        updateAddState();
        return;
      }

      addButton.disabled = true;
      setStatus("Saving your design...");

      try {
        const checkoutPreviewPng = KovaDesignContract.captureCheckoutPreviewPng(canvas);
        const draftInput = KovaDesignContract.buildInput({
          variantId: selectedVariant.id,
          text: engravingText,
          fontFamily: fontSelect.value,
          colorHex: color.value,
          colorName: color.dataset.colorName,
          sizePercent: sizeInput.value,
          xPercent: lastEngravingPosition.xPercent.toFixed(2),
          yPercent: lastEngravingPosition.yPercent.toFixed(2),
          rotationDeg: rotateInput.value,
          checkoutPreviewPng
        });
        const idempotencyKey = `idem_${crypto.randomUUID().replaceAll("-", "")}`;
        let draft;
        try {
          draft = await KovaDesignContract.saveDraft(draftInput, idempotencyKey);
        } catch (_firstSaveError) {
          setStatus("Saving your design again...");
          try {
            draft = await KovaDesignContract.saveDraft(draftInput, idempotencyKey);
          } catch (_secondSaveError) {
            throw new Error("We couldn’t confirm your design was saved. No item was added, and no order or charge occurred. Please try again.");
          }
        }
        await preloadCheckoutPreview(draft.designId);
        setStatus("Adding your lyrakova stone...");

        const response = await fetch(joinRoute("cart/add.js"), {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            items: [{
              id: Number(selectedVariant.id),
              quantity: 1,
              properties: {
                "Engraving text": engravingText,
                "Font style": fontSelect.options[fontSelect.selectedIndex].text,
                "Engraving color": `${color.dataset.colorName} (${color.value})`,
                "Engraving size": `${Math.round(Number(sizeInput.value))}%`,
                "Engraving x position": `${Number(lastEngravingPosition.xPercent.toFixed(2))}%`,
                "Engraving y position": `${Number(lastEngravingPosition.yPercent.toFixed(2))}%`,
                "Engraving rotation": `${Math.round(Number(rotateInput.value))}°`,
                "_design_id": draft.designId,
                "_design_version": String(draft.designVersion),
                "_design_checksum": draft.checksum
              }
            }]
          })
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || "Unable to add item. Please try again.");
        }

        window.location.href = joinRoute("cart");
      } catch (error) {
        updateAddState();
        setStatus(error.message || "Unable to add item. Please try again.", "error");
      }
    };

    if (stoneSelect) {
      stoneSelect.addEventListener("change", () => selectVariant(stoneSelect.value));
    }

    if (stoneTrigger && stoneListbox) {
      stoneTrigger.addEventListener("click", () => {
        setPickerOpen(stoneTrigger.getAttribute("aria-expanded") !== "true", true);
      });
      stoneTrigger.addEventListener("keydown", (event) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          setPickerOpen(true, true);
        } else if (event.key === "Escape") {
          event.preventDefault();
          setPickerOpen(false);
        }
      });
    }

    stoneOptions.forEach((option, index) => {
      option.addEventListener("click", () => {
        if (stoneSelect) {
          stoneSelect.value = option.dataset.variantId;
          stoneSelect.dispatchEvent(new Event("change", { bubbles: true }));
        } else {
          selectVariant(option.dataset.variantId);
        }
        setPickerOpen(false);
        stoneTrigger?.focus();
      });
      option.addEventListener("keydown", (event) => {
        let nextIndex = index;
        if (event.key === "ArrowDown") nextIndex = (index + 1) % stoneOptions.length;
        else if (event.key === "ArrowUp") nextIndex = (index - 1 + stoneOptions.length) % stoneOptions.length;
        else if (event.key === "Home") nextIndex = 0;
        else if (event.key === "End") nextIndex = stoneOptions.length - 1;
        else if (event.key === "Escape") {
          event.preventDefault();
          setPickerOpen(false);
          stoneTrigger?.focus();
          return;
        } else return;
        event.preventDefault();
        stoneOptions[nextIndex]?.focus();
      });
    });

    document.addEventListener("click", (event) => {
      if (stonePicker && !stonePicker.contains(event.target)) setPickerOpen(false);
    });

    if (engravingLock && stoneTrigger) {
      engravingLock.addEventListener("click", () => {
        setStatus("Choose a stone to start engraving.", "error");
        engravingLock.classList.remove("is-prompting");
        requestAnimationFrame(() => engravingLock.classList.add("is-prompting"));
        stoneTrigger.focus({ preventScroll: true });
        setPickerOpen(true, true);
      });
      engravingLock.addEventListener("animationend", () => engravingLock.classList.remove("is-prompting"));
    }

    [textInput, sizeInput, xInput, yInput, rotateInput].forEach((input) => {
      input.addEventListener("input", drawPreview);
      input.addEventListener("change", drawPreview);
    });
    fontSelect.addEventListener("change", drawPreviewAfterFontLoad);
    colorInputs.forEach((input) => input.addEventListener("change", drawPreview));
    addButton.addEventListener("click", () => {
      if (cartItemModeRequested) {
        updateCartItem();
      } else {
        addToCart();
      }
    });

    if (downloadButton) {
      downloadButton.addEventListener("click", () => {
        try {
          const link = document.createElement("a");
          link.download = `kova-engraving-preview-${Date.now()}.png`;
          link.href = canvas.toDataURL("image/png");
          link.click();
        } catch (error) {
          setStatus("Preview download is unavailable in this browser.", "error");
        }
      });
    }

    updateSelectedMeta();
    updateCount();
    setEngravingEnabled(false);
    if (cartItemModeRequested) {
      initializeCartItemEdit();
    } else {
      loadPreviewImage(product.defaultImage);
    }
  };

  const initStudios = () => {
    document.querySelectorAll("[data-kova-studio]").forEach(initStudio);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initStudios, { once: true });
  } else {
    initStudios();
  }
})();
